import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, User2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API_BASE as API } from "@/config";
import axios from "axios";

export default function InfluencerLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please fill all fields");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/influencer/login`, { email, password });
      localStorage.setItem("inf_token", res.data.token);
      localStorage.setItem("inf_data", JSON.stringify(res.data.influencer));
      toast.success("Login successful!");
      navigate("/influencer/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <User2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk' }}>Influencer Login</h1>
          <p className="text-gray-400 text-sm">Access your affiliate dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="bg-gray-900/60 backdrop-blur-xl rounded-3xl p-6 border border-gray-800 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input data-testid="inf-login-email" type="email" placeholder="you@example.com" value={email}
                onChange={e => setEmail(e.target.value)}
                className="pl-10 bg-gray-800/50 border-gray-700 text-white rounded-xl focus:border-amber-500 focus:ring-amber-500/20" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input data-testid="inf-login-password" type={showPw ? "text" : "password"} placeholder="Enter password" value={password}
                onChange={e => setPassword(e.target.value)}
                className="pl-10 pr-10 bg-gray-800/50 border-gray-700 text-white rounded-xl focus:border-amber-500 focus:ring-amber-500/20" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button data-testid="inf-login-btn" type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 font-bold rounded-2xl py-6 text-base hover:from-amber-500 hover:to-amber-700 transition-all disabled:opacity-40">
            {loading ? <span className="flex items-center gap-2"><span className="w-5 h-5 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />Logging in...</span>
              : <span className="flex items-center gap-2">Login <ArrowRight className="w-5 h-5" /></span>}
          </Button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-6">
          Don&apos;t have an account?{" "}
          <Link to="/influencer/register" className="text-amber-400 font-semibold hover:text-amber-300 transition-colors" data-testid="inf-register-link">Register here</Link>
        </p>
        <Link to="/" className="block text-center text-gray-600 text-xs mt-3 hover:text-gray-400 transition-colors">
          Back to VishuAstro
        </Link>
      </motion.div>
    </div>
  );
}
